import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, Users, Coins, Gift, Euro, Ticket, ShoppingBag, Clock,
  BarChart3, PieChart as PieIcon, Store, Wallet, Download, FileText, Trash2, RefreshCw, Calendar as CalIcon, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import DatePickerInput from "../components/DatePickerInput";
import DateRangePresets from "../components/DateRangePresets";
import KpiAlertCard from "../components/KpiAlertCard";
import PrepTimesCard from "../components/PrepTimesCard";
import GiftGroupStatsCard from "../components/GiftGroupStatsCard";
import ProximityStatsCard from "../components/ProximityStatsCard";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import BranchMultiSelect from "../components/BranchMultiSelect";

const RANGES = [
  { key: 7, label: "7 giorni" },
  { key: 14, label: "14 giorni" },
  { key: 30, label: "30 giorni" },
  { key: 90, label: "90 giorni" },
  { key: 180, label: "180 giorni" },
  { key: 365, label: "1 anno" },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

const COLORS = ["#1E40AF", "#0EA5E9", "#10B981", "#6366F1", "#EC4899", "#22D3EE", "#A855F7", "#F97316"];

const Delta = ({ cur, prev }) => {
  if (prev === undefined || prev === null) return null;
  if (!prev && !cur) return <span className="text-[10px] text-zinc-600">—</span>;
  const pct = prev ? ((cur - prev) / prev) * 100 : 100;
  const up = pct > 0, flat = Math.abs(pct) < 0.05;
  const cls = flat ? "text-zinc-500 bg-zinc-800/60" : up ? "text-emerald-300 bg-emerald-500/10" : "text-red-300 bg-red-500/10";
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${cls}`} title="vs periodo precedente">
      {flat ? "= " : up ? "▲ " : "▼ "}{Math.abs(pct) >= 1000 ? ">999" : Math.abs(pct).toFixed(pct % 1 === 0 ? 0 : 1)}%
    </span>
  );
};

const KPI = ({ label, value, icon: Icon, accent = "gold", suffix = "", testid, cur, prev }) => (
  <div className="card-elev p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center
        ${accent === "red" ? "bg-red-500/10 text-red-300" :
          accent === "gold" ? "bg-amber-500/10 text-amber-300" :
          accent === "mint" ? "bg-emerald-500/10 text-emerald-300" :
          accent === "blue" ? "bg-indigo-500/10 text-indigo-300" :
          "bg-zinc-800 text-zinc-300"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="flex items-end justify-between gap-2 flex-wrap">
      <div className="metric-value text-2xl sm:text-3xl">
        {value}<span className="text-base text-zinc-500 ml-1">{suffix}</span>
      </div>
      <span data-testid={testid ? `${testid}-delta` : undefined}><Delta cur={cur} prev={prev} /></span>
    </div>
  </div>
);

const ChartCard = ({ title, subtitle, icon: Icon, children, extra }) => (
  <div className="card-elev p-5">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-heading font-semibold text-lg">{title}</h3>
        {subtitle && <p className="text-xs text-zinc-500">{subtitle}</p>}
      </div>
      {Icon && <Icon className="h-5 w-5 text-zinc-500" />}
    </div>
    {extra}
    <div className="h-72">{children}</div>
  </div>
);

const tooltipStyle = {
  contentStyle: { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 },
  labelStyle: { color: "#475569" },
};

export default function Statistics() {
  const { user } = useAuth();
  const [range, setRange] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const customRef = useRef(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [prepTimes, setPrepTimes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [walletStats, setWalletStats] = useState(null);
  const [appleLogs, setAppleLogs] = useState(null);
  const [logsBusy, setLogsBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  // Filtro sede (admin/Owner): array vuoto = tutte le sedi
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]);

  const loadAppleLogs = async () => {
    setLogsBusy(true);
    try {
      const { data } = await api.get("/apple-pass/logs?limit=50");
      setAppleLogs(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLogsBusy(false); }
  };

  const clearAppleLogs = async () => {
    if (!window.confirm("Eliminare tutti i log Apple pass?")) return;
    try {
      await api.delete("/apple-pass/logs");
      toast.success("Log Apple eliminati");
      loadAppleLogs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadDemoPass = async () => {
    setDemoBusy(true);
    try {
      const res = await api.get("/wallet/demo.pkpass", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.apple.pkpass" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "nouvelle-demo.pkpass";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pass demo scaricato");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data;
      toast.error(typeof detail === "string" ? detail : formatApiError(err));
    } finally { setDemoBusy(false); }
  };

  const load = async (d, opts = {}) => {
    setLoading(true);
    try {
      const params = opts.start && opts.end
        ? { start: opts.start, end: opts.end }
        : { days: d };
      if (branchIds.length > 0) params.branch_id = branchIds.join(",");
      const res = await api.get(`/stats/analytics`, { params });
      setData(res.data);
      api.get(`/stats/prep-times`, { params }).then((r) => setPrepTimes(r.data)).catch(() => setPrepTimes(null));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return toast.error("Seleziona data inizio e fine");
    if (customEnd < customStart) return toast.error("La data fine deve essere >= data inizio");
    setCustomMode(true);
    load(null, { start: customStart, end: customEnd });
  };

  const clearCustom = () => {
    setCustomMode(false);
    customRef.current = false;
    setCustomStart("");
    setCustomEnd("");
    load(range);
  };

  const applyPreset = ({ from, to }) => {
    if (!from || !to) return clearCustom();
    setCustomStart(from);
    setCustomEnd(to);
    setCustomMode(true);
    customRef.current = true;
    load(null, { start: from, end: to });
  };

  const downloadExport = async (fmt) => {
    try {
      const params = customMode && customStart && customEnd
        ? { start: customStart, end: customEnd }
        : { days: range };
      if (branchIds.length > 0) params.branch_id = branchIds.join(",");
      const res = await api.get(`/stats/analytics.${fmt}`, { params, responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const label = customMode ? `${customStart}_${customEnd}` : `${range}g`;
      a.download = `statistiche_${label}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statistiche esportate in ${fmt.toUpperCase()} ✓`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    if (customMode || customRef.current) return; // custom range is handled by applyCustom/applyPreset
    load(range);
    /* eslint-disable-next-line */
  }, [range, customMode, branchIds.join(",")]);
  useEffect(() => {
    api.get("/wallet/stats").then((r) => setWalletStats(r.data)).catch(() => null);
    if (user?.role === "admin") {
      api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
    }
  }, [user]);

  const shortSeries = useMemo(() => {
    if (!data?.series) return [];
    const prev = data.prev_series || [];
    return data.series.map((r, i) => ({ ...r, label: r.date.slice(5), prev_revenue: prev[i]?.revenue ?? 0, prev_date: prev[i]?.date }));
  }, [data]);

  const shortCoupons = useMemo(() => {
    if (!data?.coupon_series) return [];
    return data.coupon_series.map((r) => ({ ...r, label: r.date.slice(5) }));
  }, [data]);

  if (!data) {
    return (
      <div className="text-zinc-500" data-testid="stats-loading">Caricamento statistiche…</div>
    );
  }

  const T = data.totals;
  const P = data.prev_totals;
  const fmtIt = (s) => (s ? s.split("-").reverse().join("/") : "");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="statistics-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="pill pill-gold">STATISTICHE</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">
            Dashboard andamento
          </h1>
          <p className="text-zinc-400 mt-1">
            {new Date(data.from_date).toLocaleDateString("it-IT")} → {new Date(data.to_date).toLocaleDateString("it-IT")}
            {data.custom_range && <span className="ml-2 text-amber-300 text-xs pill pill-gold" data-testid="stats-custom-badge">Personalizzato</span>}
          </p>
        </div>
        <div className="flex flex-col gap-2" data-testid="stats-range-selector">
          {user?.role === "admin" && branches.length > 0 && (
            <div className="w-full sm:w-64 self-end" data-testid="stats-branch-filter">
              <BranchMultiSelect
                branches={branches}
                value={branchIds}
                onChange={setBranchIds}
                testId="stats-branch"
                compact
                align="end"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                size="sm"
                variant={(!customMode && range === r.key) ? "default" : "outline"}
                onClick={() => { setCustomMode(false); setRange(r.key); }}
                data-testid={`stats-range-${r.key}`}
                className={`h-9 ${(!customMode && range === r.key)
                  ? "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] text-white"
                  : "border-zinc-800"}`}
              >
                {r.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 justify-end" data-testid="stats-export-buttons">
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadExport("xlsx")}
              data-testid="stats-export-xlsx"
              className="h-9 border-emerald-800/50 text-emerald-300 hover:bg-emerald-500/10"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Esporta Excel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadExport("pdf")}
              data-testid="stats-export-pdf"
              className="h-9 border-red-800/50 text-red-300 hover:bg-red-500/10"
            >
              <FileText className="h-3.5 w-3.5 mr-1" /> Esporta PDF
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 justify-end items-center bg-zinc-950/60 border border-zinc-800 rounded-lg p-2" data-testid="stats-custom-range">
            <div className="w-full flex justify-end">
              <DateRangePresets
                from={customMode ? customStart : ""}
                to={customMode ? customEnd : ""}
                onChange={applyPreset}
                testId="stats-date-preset"
                pageKey="stats"
              />
            </div>
            <CalIcon className="h-4 w-4 text-zinc-500 ml-1" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">dal</span>
            <DatePickerInput
              testid="stats-custom-start"
              value={customStart}
              max={customEnd || isoDate(new Date())}
              className="bg-zinc-950 border-zinc-800 h-8 w-full sm:w-40 text-sm"
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">al</span>
            <DatePickerInput
              testid="stats-custom-end"
              value={customEnd}
              min={customStart}
              max={isoDate(new Date())}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-zinc-950 border-zinc-800 h-8 w-full sm:w-40 text-sm"
            />
            <Button
              data-testid="stats-custom-apply"
              size="sm"
              onClick={applyCustom}
              disabled={loading || !customStart || !customEnd}
              className="h-8 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              Applica
            </Button>
            {customMode && (
              <Button
                data-testid="stats-custom-clear"
                size="sm"
                variant="outline"
                onClick={clearCustom}
                className="h-8 border-zinc-800"
                title="Torna ai preset"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      {P && (
        <p className="text-[11px] text-zinc-500 -mb-2" data-testid="kpi-prev-period-label">
          Variazione % vs periodo precedente ({fmtIt(data.prev_from_date)} → {fmtIt(data.prev_to_date)})
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-revenue" label="Fatturato" value={`€ ${T.revenue.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} icon={Euro} accent="red" cur={T.revenue} prev={P?.revenue} />
        <KPI testid="kpi-tx" label="Transazioni" value={T.tx_count} icon={ShoppingBag} accent="gold" cur={T.tx_count} prev={P?.tx_count} />
        <KPI testid="kpi-avg-ticket" label="Scontrino medio" value={`€ ${T.avg_ticket.toFixed(2)}`} icon={TrendingUp} accent="mint" cur={T.avg_ticket} prev={P?.avg_ticket} />
        <KPI testid="kpi-new" label="Nuovi clienti" value={T.new_customers} icon={Users} accent="blue" cur={T.new_customers} prev={P?.new_customers} />
        <KPI testid="kpi-earned" label="Punti emessi" value={T.earned} icon={Coins} accent="gold" cur={T.earned} prev={P?.earned} />
        <KPI testid="kpi-redeemed" label="Punti riscattati" value={T.redeemed} icon={Gift} accent="mint" cur={T.redeemed} prev={P?.redeemed} />
        <KPI testid="kpi-coupons" label="Coupon usati" value={T.coupons_used} icon={Ticket} accent="red" cur={T.coupons_used} prev={P?.coupons_used} />
        <KPI testid="kpi-coupons-eur" label="Sconto coupon" value={`€ ${T.coupons_discount.toFixed(2)}`} icon={Ticket} accent="red" cur={T.coupons_discount} prev={P?.coupons_discount} />
      </div>

      {/* Revenue trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard
            title="Andamento fatturato"
            subtitle="Fatturato giornaliero · linea tratteggiata = periodo precedente"
            icon={TrendingUp}
          >
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <AreaChart data={shortSeries} data-testid="chart-revenue">
                <defs>
                  <linearGradient id="rev" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#E5383B" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#E5383B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip {...tooltipStyle} formatter={(v, n, p) => n === "revenue" ? [`€ ${Number(v).toFixed(2)}`, "Fatturato"] : n === "prev_revenue" ? [`€ ${Number(v).toFixed(2)}`, `Periodo prec. (${(p?.payload?.prev_date || "").slice(5)})`] : [v, n]} />
                <Area type="monotone" dataKey="prev_revenue" stroke="#A1A1AA" strokeDasharray="5 4" strokeWidth={1.5} fill="none" dot={false} activeDot={{ r: 3 }} data-testid="chart-revenue-prev" />
                <Area type="monotone" dataKey="revenue" stroke="#E5383B" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Nuovi clienti" subtitle="Registrazioni per giorno" icon={Users}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
            <BarChart data={shortSeries} data-testid="chart-new-customers">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="new_customers" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Points emitted vs redeemed */}
      {user?.role === "admin" && <KpiAlertCard />}
      <PrepTimesCard data={prepTimes} />
      {user?.role === "admin" && <ProximityStatsCard days={customMode && customStart && customEnd ? Math.max(1, Math.round((new Date(customEnd) - new Date(customStart)) / 86400000) + 1) : Number(range) || 30} tooltipStyle={tooltipStyle} />}
      {user?.role === "admin" && <GiftGroupStatsCard gg={data.gift_groups} tooltipStyle={tooltipStyle} onRefresh={() => (customMode && customStart && customEnd ? load(null, { start: customStart, end: customEnd }) : load(range))} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Punti emessi vs riscattati" subtitle="Dinamica dei punti nel periodo" icon={Coins}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
            <LineChart data={shortSeries} data-testid="chart-points">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="earned" name="Emessi" stroke="#F59E0B" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="redeemed" name="Riscattati" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Coupon usati" subtitle="Numero e sconto in € per giorno" icon={Ticket}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
            <BarChart data={shortCoupons} data-testid="chart-coupons">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis yAxisId="left" stroke="#64748B" fontSize={11} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" stroke="#64748B" fontSize={11} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="count" name="Coupon usati" fill="#E5383B" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="discount" name="Sconto €" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top rewards & top customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top premi riscattati" subtitle="Classifica premi nel periodo" icon={Gift}>
          {data.top_rewards.length === 0 ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
              Nessun riscatto nel periodo
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <BarChart data={data.top_rewards} layout="vertical" margin={{ left: 20 }} data-testid="chart-top-rewards">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={11} width={130} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" name="Riscatti" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <div className="card-elev p-5" data-testid="top-customers-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg">Top clienti (per fatturato)</h3>
              <p className="text-xs text-zinc-500">I clienti che hanno speso di più nel periodo</p>
            </div>
            <BarChart3 className="h-5 w-5 text-zinc-500" />
          </div>
          <ul className="space-y-3 max-h-72 overflow-y-auto">
            {data.top_customers.length === 0 && (
              <li className="text-sm text-zinc-500 py-4 text-center">Nessuna spesa nel periodo</li>
            )}
            {data.top_customers.map((c, i) => (
              <li key={c.id} className="flex items-center gap-3" data-testid={`top-cust-${c.id}`}>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center font-heading font-bold text-sm
                  ${i === 0 ? "bg-amber-500/20 text-amber-300" :
                    i === 1 ? "bg-zinc-500/20 text-zinc-300" :
                    i === 2 ? "bg-orange-500/20 text-orange-300" :
                    "bg-zinc-800 text-zinc-400"}`}>
                  #{i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">
                    Carta {c.card_number} · {c.visits} visite
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-emerald-300">€ {c.revenue.toFixed(2)}</div>
                  <div className="text-[10px] text-zinc-500">{c.points} pt</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Hourly heat + Branches + Segments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Ore di picco" subtitle="Distribuzione oraria delle vendite" icon={Clock}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
            <BarChart data={data.hourly} data-testid="chart-hourly">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="hour" stroke="#64748B" fontSize={11} tickFormatter={(v) => `${v}h`} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip {...tooltipStyle} labelFormatter={(v) => `Ore ${v}:00`} />
              <Bar dataKey="count" fill="#22D3EE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {user?.role === "admin" && data.branches?.length > 0 ? (
          <ChartCard title="Fatturato per sede" subtitle="Ripartizione tra filiali" icon={Store}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <PieChart data-testid="chart-branches">
                <Pie
                  data={data.branches}
                  dataKey="revenue"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(e) => `€ ${e.revenue.toFixed(0)}`}
                >
                  {data.branches.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => `€ ${Number(v).toFixed(2)}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        ) : (
          <ChartCard title="Transazioni giornaliere" subtitle="Numero di scontrini per giorno" icon={ShoppingBag}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <LineChart data={shortSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="tx_count" name="Transazioni" stroke="#F59E0B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard title="Cluster clienti" subtitle="Distribuzione per segmento" icon={PieIcon}>
          {data.segments.filter(s => s.count > 0).length === 0 ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
              Nessun cluster popolato
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <PieChart data-testid="chart-segments">
                <Pie
                  data={data.segments.filter(s => s.count > 0)}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={90}
                  label={(e) => e.name}
                >
                  {data.segments.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Wallet adoption */}
      {walletStats && (
        <div className="card-elev p-5" data-testid="wallet-stats-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Wallet className="h-4 w-4 text-amber-300" /> Adozione Wallet
              </h3>
              <p className="text-xs text-zinc-500">
                {walletStats.total} salvataggi totali · {walletStats.unique_customers} clienti unici · {walletStats.apple_devices || 0} iPhone registrati
              </p>
              <p className="text-xs text-zinc-500" data-testid="wallet-stats-updates">
                Aggiornamenti pass: <b className="text-zinc-200">{walletStats.updates_today || 0}</b> oggi · {walletStats.updates_total || 0} totali
              </p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <span className="pill pill-mint" data-testid="wallet-stats-apple"> Apple {walletStats.by_method?.apple || 0}</span>
              <span className="pill pill-gold" data-testid="wallet-stats-google"> Google {walletStats.by_method?.google || 0}</span>
              <Button
                data-testid="wallet-demo-download"
                onClick={downloadDemoPass}
                disabled={demoBusy}
                size="sm"
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> {demoBusy ? "..." : "Scarica pass demo"}
              </Button>
              <Button
                data-testid="wallet-pdf-download"
                onClick={async () => {
                  try {
                    const res = await api.get("/wallet/stats.pdf", { responseType: "blob" });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = `wallet-report-${new Date().toISOString().slice(0,10)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Report PDF scaricato");
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                size="sm"
                variant="outline"
                className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
              >
                <FileText className="h-3.5 w-3.5 mr-1" /> Report PDF
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Apple vs Google</div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Apple", value: walletStats.by_method?.apple || 0 },
                        { name: "Google", value: walletStats.by_method?.google || 0 },
                      ]}
                      dataKey="value" cx="50%" cy="50%" outerRadius={60} label={(e) => e.name}
                    >
                      <Cell fill="#F4F4F5" />
                      <Cell fill="#F59E0B" />
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Piattaforma</div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                  <PieChart>
                    <Pie
                      data={Object.entries(walletStats.by_platform || {}).map(([k, v]) => ({ name: k, value: v }))}
                      dataKey="value" cx="50%" cy="50%" outerRadius={60} label={(e) => e.name}
                    >
                      {Object.keys(walletStats.by_platform || {}).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">A/B test bottone</div>
              <div className="space-y-1 text-sm mt-3">
                {Object.entries(walletStats.by_variant || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between p-2 rounded bg-zinc-950 border border-zinc-900" data-testid={`wallet-variant-${k}`}>
                    <span className="text-zinc-400">{k === "icon" ? "Solo icona" : k === "long" ? "Testo lungo" : k}</span>
                    <span className="font-mono text-amber-300">{v}</span>
                  </div>
                ))}
                {(!walletStats.by_variant || Object.keys(walletStats.by_variant).length === 0) && (
                  <div className="text-xs text-zinc-500 py-2">Nessun dato ancora</div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Trend 14 giorni · salvataggi Apple vs Google e aggiornamenti pass</div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                <LineChart data={walletStats.series_14d.map((r) => ({ ...r, label: r.date.slice(5) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="apple" stroke="#F4F4F5" strokeWidth={2} dot={false} name="Apple" />
                  <Line type="monotone" dataKey="updates" stroke="#10B981" strokeWidth={2} dot={false} name="Aggiornamenti" />
                  <Line type="monotone" dataKey="google" stroke="#F59E0B" strokeWidth={2} dot={false} name="Google" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-zinc-500 text-center pb-2">
        {loading ? "Aggiornamento in corso…" : `Dati aggiornati a ${new Date().toLocaleTimeString("it-IT")}`}
      </div>

      {/* Apple Pass debug logs */}
      <div className="card-elev p-5" data-testid="apple-logs-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
              <FileText className="h-4 w-4 text-sky-300" /> Log Apple Wallet
            </h3>
            <p className="text-xs text-zinc-500">
              Messaggi inviati dai server Apple al nostro webhook — utili per diagnosticare errori sui pass in produzione
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="apple-logs-refresh" onClick={loadAppleLogs} disabled={logsBusy} size="sm" variant="outline" className="border-zinc-800">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${logsBusy ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
            {appleLogs?.total > 0 && (
              <Button data-testid="apple-logs-clear" onClick={clearAppleLogs} size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Pulisci
              </Button>
            )}
          </div>
        </div>
        {!appleLogs && (
          <div className="text-xs text-zinc-500 py-6 text-center">
            Clicca "Aggiorna" per caricare i log
          </div>
        )}
        {appleLogs && appleLogs.total === 0 && (
          <div className="text-xs text-zinc-500 py-6 text-center">
            Nessun log · Apple invia log qui solo quando qualcosa va storto sul pass
          </div>
        )}
        {appleLogs && appleLogs.items?.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              {appleLogs.items.length} di {appleLogs.total} log · più recenti in cima
            </div>
            {appleLogs.items.map((entry) => (
              <details key={entry.id} className="p-2 rounded bg-zinc-950 border border-zinc-800 text-xs" data-testid={`apple-log-${entry.id}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <span className="text-zinc-400">{new Date(entry.created_at).toLocaleString("it-IT")}</span>
                  <span className="text-amber-300">{(entry.logs || []).length} messaggi</span>
                </summary>
                <div className="mt-2 space-y-1 font-mono text-[10px] text-zinc-300">
                  {(entry.logs || []).map((l, i) => (
                    <div key={i} className="p-1.5 rounded bg-black/50 border border-zinc-900">{l}</div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
